import capitalize from 'lodash/capitalize';

import { getGender } from 'utils/gender';
import { getProduct } from 'utils/product';
import { dispatch, on } from 'utils/theme';
import { uniqueName } from 'utils/variant';

class Tracking {
  static startup() {
    this.registerTracking();
    this.initThemeEvents();

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        this.initClickTracking();
        this.initPromotions();
        this.initItemListImpressions();
        this.initPageEvents();
      });
    } else {
      this.initClickTracking();
      this.initPromotions();
      this.initItemListImpressions();
      this.initPageEvents();
    }
  }

  static getLevel(index) {
    switch (index) {
      case 1:
        return 'First Level';

      case 2:
        return 'Second Level';

      case 3:
        return 'Third Level';

      case 4:
        return 'Fourth Level';

      case 5:
        return 'Fifth Level';

      case 6:
        return 'Sixth Level';

      case 7:
        return 'Seventh Level';

      case 8:
        return 'Eighth Level';

      case 9:
        return 'Ninth Level';

      case 10:
        return 'Tenth Level';

      default:
        return index.toString();
    }
  }

  static mapCartItems(items) {
    return items.map((item) => ({
      item_category: item.product_type,
      item_id: item.product_id,
      item_name: item.product_title,
      item_variant: item.variant_title,
      item_sku: item.sku,
      price: typeof item.price === 'number' ? item.price / 100 : parseFloat(item.price),
      quantity: item.quantity,
      affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
      coupon: item.applied_discounts?.length ? item.applied_discounts[0].description : undefined,
      discount: item.applied_discounts?.length
        ? parseFloat(item.applied_discounts[0].amount)
        : undefined,
    }));
  }

  static formatPrice(price) {
    return (price / 100).toFixed(2);
  }

  static registerTracking() {
    const search = new URLSearchParams(window.location.search);
    const affiliation = search.get('affiliation');
    const { Shopify: allowed } = window.theme.consent || { Shopify: false };

    if (affiliation && allowed) {
      localStorage.setItem('tracking-affiliation', affiliation);
    }
  }

  static initClickTracking() {
    const elements = document.querySelectorAll('[data-tracking="click"]:not([data-mounted])');

    elements.forEach((element) => {
      element.addEventListener('click', (clickEvent) => {
        const {
          event,
          eventCategory,
          eventLabel,
          eventValue,
          pdpParameter,
          aboutUsParameter,
          preventNavigation,
          preventMobileNavigation,
          collectionParameter,
          navParameterAction,
          navParameterLabel,
          navParameterType,
          navParameterGender,
          searchParameter,
        } = element.dataset;

        let { eventAction } = element.dataset;

        if (eventAction === '%SECTION_ORDER%') {
          const container = element.closest('.shopify-section');
          const index =
            [...container.parentElement.children]
              .filter((node) => node.tagName === 'SECTION')
              .indexOf(container) + 1;

          eventAction = this.getLevel(index);
        }

        window.dataLayer.push({
          event,
          ...(eventCategory ? { eventCategory } : {}),
          ...(eventAction ? { eventAction } : {}),
          ...(eventLabel ? { eventLabel } : {}),
          ...(eventValue ? { eventValue } : {}),
          ...(pdpParameter ? { pdp_parameter: pdpParameter } : {}),
          ...(aboutUsParameter ? { about_us_parameter: aboutUsParameter } : {}),
          ...(collectionParameter ? { collection_parameter: collectionParameter } : {}),
          ...(navParameterAction ? { nav_parameter_action: navParameterAction } : {}),
          ...(navParameterLabel ? { nav_parameter_label: navParameterLabel } : {}),
          ...(navParameterType ? { nav_parameter_type: navParameterType } : {}),
          ...(navParameterGender !== undefined
            ? { nav_parameter_gender: capitalize(getGender()) }
            : {}),
          ...(searchParameter ? { search_parameter: searchParameter } : {}),
        });

        if (
          element.href &&
          element.href !== window.location.href &&
          clickEvent.defaultPrevented === false
        ) {
          clickEvent.preventDefault();
          const shouldPreventNavigation =
            preventNavigation !== undefined ||
            (preventMobileNavigation !== undefined && theme.isMobile);

          if (!shouldPreventNavigation) {
            setTimeout(() => {
              window.location.href = element.href;
            }, 50);
          }
        }
      });

      Tracking.mount(element);
    });
  }

  static initSectionPromotions(element, currentPageType) {
    const sectionItemElements = element.querySelectorAll(
      currentPageType === 'about-us'
        ? '[data-promotion-item], #insta-feed'
        : '[data-promotion-item]',
    );
    const aboutUsColumnsContainer = document.querySelector('[data-about-us-columns]');

    if (currentPageType !== 'about-us') {
      const promotionItemElements = document.querySelectorAll('[data-promotion-item]');
      const section = element.closest('.shopify-section');
      const sectionIndex = Array.from(section.parentElement.childNodes).indexOf(section);

      Array.from(sectionItemElements).forEach((item, index) => {
        const itemIndex = Array.from(promotionItemElements).indexOf(item);
        const itemSlot = item.dataset.promotionSlot || `${index + 1}/${sectionItemElements.length}`;
        const itemLocationId = `${currentPageType}/${sectionIndex + 1}`;

        item.dataset.promotionIndex = itemIndex + 1;
        item.dataset.promotionSlot = itemSlot;
        item.dataset.promotionLocationId = itemLocationId;
      });
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const intersectingEntries = entries.filter((entry) => entry.isIntersecting);
        if (intersectingEntries.length && currentPageType === 'about-us') {
          intersectingEntries.forEach((entry) => {
            window.dataLayer.push({
              event: 'about_us_sectionview',
              about_us_parameter: entry.target.dataset.promotionItem || 'about-us-insta-feed',
              about_us_progression: `${Math.round(
                (aboutUsColumnsContainer.scrollLeft /
                  (aboutUsColumnsContainer.scrollWidth - window.innerWidth)) *
                100,
              )}%`,
            });
          });
        } else if (intersectingEntries.length) {
          const itemsObjects = intersectingEntries.map((entry) => {
            const itemObj = {
              promotion_name: entry.target.dataset.promotionItem,
              creative_name: entry.target.dataset.promotionIndex,
              creative_slot: entry.target.dataset.promotionSlot,
              location_id: entry.target.dataset.promotionLocationId,
            };

            observer.unobserve(entry.target);
            return itemObj;
          });

          window.dataLayer.push({
            event: 'view_promotion',
            ecommerce: {
              items: itemsObjects,
            },
          });
        }
      },
      { rootMargin: currentPageType === 'about-us' ? '0px -50% 0px -50%' : '0px 0px 0px 0px' },
    );

    Array.from(sectionItemElements).forEach((sectionItemElement) => {
      observer.observe(sectionItemElement);
    });

    element.querySelectorAll('[data-promotion-item] a').forEach((link) => {
      link.addEventListener('click', (event) => {
        const linkNotPrevented = link.href && event.defaultPrevented === false;
        if (linkNotPrevented) {
          event.preventDefault();
        }

        if (currentPageType === 'about-us') {
          window.dataLayer.push({
            event: 'about_us_linkclick',
            about_us_parameter: link.href,
          });
        } else {
          const promotionItemElement = event.currentTarget.hasAttribute('data-promotion-item')
            ? event.currentTarget
            : event.currentTarget.closest('[data-promotion-item]');
          const itemObj = {
            promotion_name: promotionItemElement.dataset.promotionItem,
            creative_name: promotionItemElement.dataset.promotionIndex,
            creative_slot: promotionItemElement.dataset.promotionSlot,
            location_id: promotionItemElement.dataset.promotionLocationId,
          };

          window.dataLayer.push({
            event: 'select_promotion',
            ecommerce: {
              items: [itemObj],
            },
          });

          if (currentPageType === 'collection') {
            window.dataLayer.push({
              event: 'collection_category_click',
              collection_parameter: promotionItemElement.dataset.promotionItem,
            });
          }
        }

        if (linkNotPrevented) {
          setTimeout(() => {
            window.location.href = link.href;
          }, 50);
        }
      });
    });
  }

  static initPromotions() {
    const promotionElements = document.querySelectorAll('[data-promotion]');
    const currentPageType = window.theme.pageType.replace('index', 'homepage');

    promotionElements.forEach((element) => {
      this.initSectionPromotions(element, currentPageType);
    });

    if (currentPageType === 'collection') {
      document.addEventListener('theme:collection:populated', () => {
        const promotionCollection = document.querySelector('[data-promotion="collection"]');

        if (promotionCollection) this.initSectionPromotions(promotionCollection, currentPageType);
      });
    } else if (currentPageType === 'page' && window.theme.templateSuffix.includes('slider')) {
      this.initSectionPromotions(document, 'about-us');
    }
  }

  static async trackViewItemList(detail) {
    const { productHandles, placement } = detail;
    let { products } = detail;

    if (!products?.length && productHandles?.length) {
      const promises = productHandles.map((handle) => getProduct(handle));
      products = await Promise.all(promises);
    } else if (!products?.length && !productHandles?.length) {
      return;
    }

    window.dataLayer.push({
      event: 'view_item_list',
      placement,
      view_item_list_items: products.map((product, index) => ({
        item_category: product.type || product.product_type,
        item_id: product.id || product.product_id,
        item_name: product.title || product.product_title,
        price: (`${product.price}` || product.variants[0].price) / 100,
        index: product.index || index,
      })),
    });
  }

  static async trackSelectItem({ product, placement }) {
    window.dataLayer.push({
      event: 'select_item',
      placement,
      items: [
        {
          item_category: product.type,
          item_id: product.id,
          item_name: product.title,
          price: product.price,
          affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
        },
      ],
    });
  }

  static getProductItemIndex(item) {
    const container = item.closest('[data-product-items-container]');
    const containerProductItems = container.querySelectorAll('[data-product-item]');
    const index = Array.from(containerProductItems).indexOf(item);

    return index;
  }

  static getProductItemPlacement(item) {
    const container = item.closest('[data-product-items-container]');

    return container.dataset.productItemsContainer;
  }

  static selectItemCallback(event, imagesSlider = true, preventDefault = false) {
    const { target } = event;

    if (
      target.matches(
        imagesSlider
          ? '[data-product-items-container] [data-product-item] a[data-product-link], [data-product-items-container] [data-product-item] a[data-product-link] .swiper-wrapper *'
          : '[data-product-items-container] a[data-product-item][data-product-link], [data-product-items-container] a[data-product-item][data-product-link] *, [data-product-items-container] [data-product-item] a[data-product-link], [data-product-items-container] [data-product-item] a[data-product-link] *',
      )
    ) {
      const link = target.closest('[data-product-link]');

      if (preventDefault && link) {
        event.preventDefault();
        setTimeout(() => {
          window.location.href = link.href;
        }, 0);
      }

      const item = target.closest('[data-product-item]');
      const product = JSON.parse(item.dataset.productItem || '{}');

      try {
        this.trackSelectItem({
          product: {
            ...product,
            index: product.index || this.getProductItemIndex(item),
            placement: product.placement || this.getProductItemPlacement(item),
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
  }

  static initItemListImpressions() {
    const elements = document.querySelectorAll('[data-item-list]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting === false) {
          return;
        }

        this.trackViewItemList({
          products: Array.from(entry.target.querySelectorAll('[data-product-item]'))?.map((item) =>
            JSON.parse(item.dataset.productItem || '{}'),
          ),
          placement: entry.target.dataset.itemList,
        });

        observer.unobserve(entry.target);
      });
    });

    elements.forEach((element) => observer.observe(element));
  }

  static initThemeEvents() {
    on('add-to-cart', ({ detail }) => {
      const { product, variant, quantity, items, placement, isRelewiseForm } = detail;

      if (isRelewiseForm) {
        return;
      }


      if (items && items?.length) {
        window.dataLayer.push({
          event: 'add_to_cart',
          value: items.reduce(
            (acc, { variant: itemVariant, price }) => acc + (itemVariant?.price || price) / 100,
            0,
          ),
          currency: Shopify.currency.active,
          placement,
          items: items.map(
            ({
              product: itemProduct,
              variant: itemVariant,
              quantity: itemQuantity,
              product_type: productType,
              product_id: productId,
              product_title: productTitle,
              variant_title: variantTitle,
              sku,
              price,
            }) => ({
              item_category: itemProduct?.type || productType,
              item_id: itemProduct?.id || productId,
              item_name: itemProduct?.title || productTitle,
              item_variant: itemVariant?.public_title || variantTitle,
              item_sku: sku,
              price: (itemVariant?.price || price) / 100,
              quantity: itemProduct ? itemQuantity : quantity,
            }),
          ),
        });
      } else {
        window.dataLayer.push({
          event: 'add_to_cart',
          value: variant.price / 100,
          currency: Shopify.currency.active,
          placement,
          items: [
            {
              item_category: product.type,
              item_id: product.id,
              item_name: product.title,
              item_variant: variant.public_title,
              item_sku: variant.sku,
              price: variant.price / 100,
              quantity,
            },
          ],
        });
      }
    });

    on('remove-from-cart', ({ detail }) => {
      const { item, quantity } = detail;

      window.dataLayer.push({
        event: 'remove_from_cart',
        value: item.price / 100,
        currency: Shopify.currency.active,
        items: [
          {
            item_category: item.product_type,
            item_id: item.product_id,
            item_name: item.product_title,
            item_variant: item.variant_title,
            item_sku: item.sku,
            price: item.price / 100,
            affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
            quantity,
          },
        ],
      });
    });

    on('view-cart', ({ detail }) => {
      const cart = detail?.cart || window.theme.cart.initialCart;

      window.dataLayer.push({
        event: 'view_cart',
        value: cart.items_subtotal_price / 100,
        currency: Shopify.currency.active,
        items: this.mapCartItems(cart.items),
      });
    });

    on('add-to-wishlist', async ({ detail }) => {
      const product = await getProduct(detail.handle);

      let price;
      if (detail.id) {
        const variant = product.variants.find((item) => item.id === detail.id);
        price = variant.price / 100;
      } else {
        price = product.variants[0].price / 100;
      }

      window.dataLayer.push({
        event: 'add_to_wishlist',
        value: price,
        currency: Shopify.currency.active,
        items: [
          {
            item_category: product.type,
            item_id: product.id,
            item_name: product.title,
            price,
            affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
          },
        ],
      });
    });

    on('remove-from-wishlist', async ({ detail }) => {
      const product = await getProduct(detail.handle);

      window.dataLayer.push({
        event: 'remove_from_wishlist',
        value: product.variants[0].price / 100,
        currency: Shopify.currency.active,
        items: [
          {
            item_category: product.type,
            item_id: product.id,
            item_name: product.title,
            price: product.variants[0].price / 100,
            affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
          },
        ],
      });
    });

    on('pdp-enlarge-main-image', () =>
      window.dataLayer.push({
        event: 'pdp_enlarge_main_image',
      }),
    );

    on('pdp-change-main-image', ({ detail }) => {
      const { index, overlay = false } = detail;

      window.dataLayer.push({
        event: 'pdp_change_main_image',
        pdp_parameter: index,
        overlay,
      });
    });

    on('pdp-open-type-selector', () =>
      window.dataLayer.push({
        event: 'pdp_open_type_selector',
      }),
    );

    on('pdp-open-type-guide', () =>
      window.dataLayer.push({
        event: 'pdp_open_type_guide',
      }),
    );

    on('pdp-open-size-selector', () =>
      window.dataLayer.push({
        event: 'pdp_open_size_selector',
      }),
    );

    on('pdp-select-size', ({ detail }) => {
      const { size } = detail;

      window.dataLayer.push({
        event: 'pdp_select_size',
        pdp_parameter: size,
      });
    });

    on('pdp-open-size-guide', () =>
      window.dataLayer.push({
        event: 'pdp_open_size_guide',
      }),
    );

    on('pdp-click-accordion-exp', ({ detail }) => {
      const { title } = detail;

      window.dataLayer.push({
        event: 'pdp_click_accordion_exp',
        pdp_parameter: title,
      });
    });

    on('pdp-click-accordion-col', ({ detail }) => {
      const { title } = detail;

      window.dataLayer.push({
        event: 'pdp_click_accordion_col',
        pdp_parameter: title,
      });
    });

    on('pdp-signup-backinstock', ({ detail }) => {
      const { type } = detail;

      window.dataLayer.push({
        event: 'pdp_signup_backinstock',
        pdp_parameter: type,
      });
    });

    on('pdp-click-readmore', ({ detail }) => {
      const { title } = detail;

      window.dataLayer.push({
        event: 'pdp_click_readmore',
        pdp_parameter: title,
      });
    });

    on('pdp-show-more-color-options', () => {
      window.dataLayer.push({
        event: 'pdp_collapse_colorswatch',
      });
    });

    on('add-contact-information', ({ detail }) => {
      const { cart, coupon } = detail;

      window.dataLayer.push({
        event: 'begin_checkout',
        value: cart.total_price / 100,
        currency: Shopify.currency?.active ?? Shopify.Checkout.currency,
        items: this.mapCartItems(cart.items),
        coupon,
      });
    });

    on('added-contact-information', ({ detail }) => {
      const { cart, coupon } = detail;

      window.dataLayer.push({
        event: 'begin_checkout',
        value: cart.total_price / 100,
        currency: Shopify.currency?.active ?? Shopify.Checkout.currency,
        items: this.mapCartItems(cart.items),
        coupon,
      });
    });

    on('add-shipping-info', ({ detail }) => {
      const { cart, coupon, method } = detail;

      window.dataLayer.push({
        event: 'add_shipping_info',
        value: cart.total_price / 100,
        currency: Shopify.currency?.active ?? Shopify.Checkout.currency,
        items: this.mapCartItems(cart.items),
        coupon,
        shipping_tier: method,
      });
    });

    on('added-shipping-info', ({ detail }) => {
      const { cart, coupon, method } = detail;

      window.dataLayer.push({
        event: 'add_shipping_info',
        value: cart.total_price / 100,
        currency: Shopify.currency?.active ?? Shopify.Checkout.currency,
        items: this.mapCartItems(cart.items),
        coupon,
        shipping_tier: method,
      });
    });

    on('add-payment-info', ({ detail }) => {
      const { cart, totalPrice, coupon, method } = detail;

      const price = totalPrice ? parseInt(totalPrice, 10) : cart.total_price;
      const methodName = price > 0 ? method : 'Free';

      window.dataLayer.push({
        event: 'add_payment_info',
        value: price / 100,
        currency: Shopify.currency?.active ?? Shopify.Checkout.currency,
        items: this.mapCartItems(cart.items),
        coupon,
        payment_type: methodName,
      });
    });

    on('added-payment-info', ({ detail }) => {
      const { cart, totalPrice, coupon, method } = detail;

      const price = totalPrice ? parseInt(totalPrice, 10) : cart.total_price;
      const methodName = price > 0 ? method : 'Free';

      window.dataLayer.push({
        event: 'add_payment_info',
        value: price / 100,
        currency: Shopify.currency?.active ?? Shopify.Checkout.currency,
        items: this.mapCartItems(cart.items),
        coupon,
        payment_type: methodName,
      });
    });

    on('purchase', ({ detail }) => {
      const { checkout } = detail;

      window.dataLayer.push({
        event: 'purchase',
        transaction_id: `${checkout.order_id}`,
        value: checkout.total_price / 100,
        shipping: checkout.shipping_price / 100,
        tax: checkout.tax_price / 100,
        customer_email: checkout.customer.email,
        customer_status: checkout.customer.orders_count > 1 ? 'Existing' : 'New',
        items: checkout.line_items.map((item) => ({
          item_category: item.product_type,
          item_id: item.product_id,
          item_name: item.product_title,
          item_variant: item.variant_title,
          item_sku: item.sku,
          price: item.original_price / 100,
          quantity: item.quantity,
          affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
          coupon: item.discount_allocations.length
            ? item.discount_allocations[0].discount_application.title.toUpperCase()
            : undefined,
          discount: item.discount_allocations.length
            ? item.discount_allocations[0].amount / 100
            : undefined,
        })),
        coupon: checkout.discount_applications.length
          ? checkout.discount_applications[0].title.toUpperCase()
          : undefined,
        discount: checkout.discount_applications.length
          ? checkout.discount_applications[0].total_allocated_amount / 100
          : undefined,
        affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
        currency: checkout.currency,
      });
    });

    on('view-item', async ({ detail }) => {
      const { productHandle, overlay } = detail;
      let { product } = detail;

      product = product || (await getProduct(productHandle));

      if (product?.title) {
        window.dataLayer.push({
          event: 'view_item',
          value: product.variants[0].price / 100,
          currency: Shopify.currency.active,
          overlay,
          items: [
            {
              item_category: product.type,
              item_id: product.id,
              item_name: product.title,
              price: product.variants[0].price / 100,
              affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
              variant_availability: product.variants.map((variant) => ({
                variant: uniqueName(product, variant, '/'),
                available: variant.available,
              })),
            },
          ],
        });
      }
    });

    on('activate-a2c-overlay', async ({ detail }) => {
      const { productHandle } = detail;
      const product = await getProduct(productHandle);

      if (product?.title)
        window.dataLayer.push({
          event: 'activate_a2c_overlay',
          value: product.variants[0].price / 100,
          currency: Shopify.currency.active,
          overlay: true,
          items: [
            {
              item_category: product.type,
              item_id: product.id,
              item_name: product.title,
              price: product.variants[0].price / 100,
              affiliation: localStorage.getItem('tracking-affiliation') ?? undefined,
              variant_availability: product.variants.map((variant) => ({
                variant: uniqueName(product, variant, '/'),
                available: variant.available,
              })),
            },
          ],
        });
    });

    on('account-open-modal', ({ detail }) => {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'pts_account_modal',
        pts_account_parameter: `account_${detail.name}`,
      });
    });

    on('point-shop-redeem', ({ detail }) => {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'spend_virtual_currency',
        user_id: detail.details.id,
        virtual_currency_name: 'points',
        item_name: detail.item.title,
        item_id: detail.product?.id,
        item_category: detail.product ? 'Internal Product' : 'Discount Voucher',
        value: detail.item.price,
        points: detail.details.balance,
        tier: detail.details.tier,
      });

      window.dataLayer.push({
        event: 'pts_redeem_item',
        value: detail.item.price,
        items: [
          {
            item_name: detail.item.title,
            item_id: detail.product?.id,
            item_category: detail.product ? 'Internal Product' : 'Discount Voucher',
            price: detail.item.price,
            quantity: 1,
          },
        ],
      });

      if (detail.product) {
        window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        window.dataLayer.push({
          event: 'pts_add_to_cart',
          value: detail.item.price,
          items: [
            {
              item_category: 'Internal Product',
              item_id: detail.product.id, // id of the product
              item_name: detail.product.title,
              price: detail.item.price,
              quantity: 1,
            },
          ],
        });
      }
    });

    on('point-shop-view-list', ({ detail }) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'pts_view_item_list',
        items: detail.items.map((item, index) => ({
          item_category: item.type,
          item_id: item.product?.id,
          item_name: item.name,
          price: item.price,
          index,
          available: item.available,
        })),
      });
    });

    on('point-shop-redeem-error', ({ detail }) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'pts_redeem_item_error',
        items: [
          {
            item_name: detail.item.title,
            item_id: detail.product?.id,
            item_category: detail.product ? 'Internal Product' : 'Discount Voucher',
            price: detail.item.price,
            quantity: 1,
          },
        ],
      });
    });

    on('point-shop-select-item', ({ detail }) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pts_select_size',
        pts_parameter: detail.variant.title,
      });

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'pts_view_item',
        value: detail.price,
        items: [
          {
            item_category: 'Internal Product',
            item_id: detail.product.id,
            item_name: detail.product.title,
            price: detail.price,
          },
        ],
      });
    });

    on('point-shop-item-open-selector', () => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pts_open_size_selector',
      });
    });

    on('login', ({ detail }) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'login',
        user_id: detail.id,
        points: detail.points,
        tier: detail.tier,
      });
    });

    on('logout', ({ detail }) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'logout',
        user_id: detail.id,
      });
    });

    on('filter-open', ({ detail }) => {
      const { value } = detail;

      window.dataLayer.push({
        event: 'filter_open',
        filter_parameter: value,
      });
    });

    on('filter-change-display', ({ detail }) => {
      const { value } = detail;

      window.dataLayer.push({
        event: 'filter_change_list_display',
        filter_parameter: value,
      });
    });

    on('filter-accordion-toggle', ({ detail }) => {
      const { type, action } = detail;

      window.dataLayer.push({
        event: 'filter_modal_accordion',
        filter_parameter: `${type}_${action}`,
      });
    });

    on('filter-select', ({ detail }) => {
      const { type, option } = detail;

      window.dataLayer.push({
        event: 'filter_select',
        filter_parameter: `${type}_${option}`,
      });
    });

    on('filter-apply', ({ detail }) => {
      const { color, fit, size, sort } = detail;

      window.dataLayer.push({
        event: 'filter_apply',
        filter_color_parameter: color,
        filter_size_parameter: size,
        filter_fit_parameter: fit,
        filter_sorting_parameter: sort,
      });
    });

    on('filter-reset-all', () => {
      window.dataLayer.push({
        event: 'filter_reset',
      });
    });

    on('filter-reset-option', ({ detail }) => {
      const { type, option } = detail;

      window.dataLayer.push({
        event: 'filter_reset_option',
        filter_parameter: `${type}_${option}`, // only if this has been changed from the default option
      });
    });

    // Suit Selector Tracking
    on('suit-selector-open', () => {
      window.dataLayer.push({
        event: 'pdp_open_size_selector_suite_modal',
      });
    });

    on('suit-selector-open-product', ({ detail }) => {
      const { suitParameter } = detail;

      window.dataLayer.push({
        event: 'pdp_open_size_selector',
        pdp_suit_parameter: suitParameter,
      });
    });

    on('suit-selector-select-fit-option', async ({ detail }) => {
      const { fit, productHandle } = detail;

      const product = await getProduct(productHandle);

      window.dataLayer.push({
        event: 'pdp_select_type',
        pdp_parameter: fit, // string: the type selected by the user
        pdp_suit_parameter: product.title, // string: the product of the suite selected
      });
    });

    on('suit-selector-select-size-option', ({ detail }) => {
      const { sizeValue, suitParameter } = detail;

      window.dataLayer.push({
        event: 'pdp_select_size',
        pdp_parameter: sizeValue, // string: the size selected by the user
        pdp_suit_parameter: suitParameter, // string: the product of the suit that was selected size for and indication on how many of the suit products have been added with this event (fx pants 1:2, blazer 2:2, add_on:shirt, add_on:vest )
      });
    });

    on('suit-selector-open-size-guide', ({ detail }) => {
      const { productName } = detail;

      window.dataLayer.push({
        event: 'pdp_open_size_guide',
        pdp_suit_parameter: productName,
      });
    });

    on('suit-selector-open-size-chart', ({ detail }) => {
      const { suitParameter } = detail;

      window.dataLayer.push({
        event: 'pdp_open_size_chart',
        pdp_suit_parameter: suitParameter,
      });
    });

    on('suit-selector-add-to-cart', async ({ detail }) => {
      const { productHandle, suitParameter } = detail;

      const product = await getProduct(productHandle);

      window.dataLayer.push({
        pdp_suit_parameter: suitParameter,
        event: 'add_to_cart',
        value: product.variants[0].price / 100,
        currency: Shopify.currency.active,
        placement: 'suit_selector_form',
        items: [
          {
            item_category: product.type,
            item_id: product.id,
            item_name: product.title,
            price: product.variants[0].price / 100,
            item_variant: uniqueName(product, product.variants[0], '/'), // Variant if available
            item_sku: product.variants[0].sku, // SKU if available
            quantity: 1, // number of the product item involved in the event
          },
        ],
      });
    });

    on('suit-selector-view-item', async ({ detail }) => {
      const { productHandle } = detail;

      const product = await getProduct(productHandle);

      window.dataLayer.push({
        event: 'view_item',
        value: product.variants[0].price / 100,
        currency: Shopify.currency.active,
        overlay: true,
        items: [
          {
            item_category: product.type,
            item_id: product.id,
            item_name: product.title,
            price: product.variants[0].price / 100,
            item_variant: uniqueName(product, product.variants[0], '/'), // Variant if available
            item_sku: product.variants[0].sku, // SKU if available
          },
        ],
      });
    });

    on('suit-selector-continue-to-cart', () => {
      window.dataLayer.push({
        event: 'pdp_continue_to_cart',
      });
    });

    on('navigation-open', ({ detail }) => {
      const { label, gender } = detail;

      window.dataLayer.push({
        event: 'navigation',
        nav_parameter_action: 'Open',
        nav_parameter_label: label,
        nav_parameter_gender: capitalize(gender),
      });
    });

    on('search-open', () => {
      window.dataLayer.push({ event: 'search_open ' });
    });

    on('view-search-results', ({ detail }) => {
      const { searchParameter } = detail;

      window.dataLayer.push({
        event: 'view_search_results',
        search_parameter: searchParameter,
      });
    });

    on('search-link-click', ({ detail }) => {
      const { searchParameter } = detail;

      window.dataLayer.push({
        event: 'search_link_click',
        search_parameter: searchParameter,
      });
    });

    on('search-change-gender', ({ detail }) => {
      const { searchParameter } = detail;

      window.dataLayer.push({
        event: 'search_change_gender',
        search_parameter: searchParameter,
      });
    });
  }

  static initPageEvents() {
    if (theme.pageType === 'product') {
      const jsData = document.querySelector('.js-data');
      const product = jsData
        ? JSON.parse(document.querySelector('.js-data').innerHTML)
        : window.meta.product;

      dispatch('view-item', {
        product,
        overlay: false,
      });
    } else if (theme.pageType === 'cart') {
      const { initialCart: cart } = window.theme.cart;

      window.dataLayer.push({
        event: 'view_cart',
        value: (cart.items_subtotal_price || 0) / 100,
        currency: Shopify.currency.active,
        items: this.mapCartItems(cart.items),
      });
    }
  }

  static mount(element) {
    element.setAttribute('data-mounted', '');
  }
}

export default Tracking;
